import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  addSubject,
  incrementMissed,
  toggleExpand,
  updateNotes,
  decrementMissed,
  updateSubject,
  deleteSubject,
  updateTimetableSlot,
  setTimetableView
} from '../features/classes/classesSlice';

const getImageForSubject = async (name) => {
  const response = await fetch(`https://api.unsplash.com/search/photos?query=${name}&client_id=YOUR_UNSPLASH_API_KEY`);
  const data = await response.json();
  return data.results[0]?.urls?.thumb || '';
};

const ClassesPage = () => {
  const dispatch = useDispatch();
  const { subjects, timetable, timetableView } = useSelector(state => state.classes);
  const [newSubject, setNewSubject] = useState('');
  const [images, setImages] = useState({});
  const [editingSubject, setEditingSubject] = useState(null);
  const [editSubjectName, setEditSubjectName] = useState('');
  const [editMissedCount, setEditMissedCount] = useState(0);
  const [showTimetable, setShowTimetable] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [newClass, setNewClass] = useState({ subject: '', teacher: '', room: '', type: 'lecture' });

  const timeSlots = ['9:00-10:00', '10:00-11:00', '11:00-12:00', '12:00-1:00', '1:00-2:00', '2:00-5:00'];
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

  const handleAddSubject = async () => {
    if (newSubject.trim() === '') return;
    dispatch(addSubject(newSubject));
    const imageUrl = await getImageForSubject(newSubject);
    setImages(prev => ({ ...prev, [newSubject]: imageUrl }));
    setNewSubject('');
  };

  const handleEditSubject = (subject) => {
    setEditingSubject(subject.id);
    setEditSubjectName(subject.name);
    setEditMissedCount(subject.missedCount);
  };

  const handleUpdateSubject = async () => {
    if (editSubjectName.trim() === '') return;
    
    const oldName = subjects.find(sub => sub.id === editingSubject)?.name;
    
    dispatch(updateSubject({
      id: editingSubject,
      name: editSubjectName,
      missedCount: editMissedCount
    }));

    if (oldName !== editSubjectName) {
      const imageUrl = await getImageForSubject(editSubjectName);
      setImages(prev => ({ 
        ...prev, 
        [editSubjectName]: imageUrl 
      }));
      setImages(prev => {
        const newImages = { ...prev };
        delete newImages[oldName];
        return newImages;
      });
    }

    setEditingSubject(null);
    setEditSubjectName('');
    setEditMissedCount(0);
  };

  const handleCancelEdit = () => {
    setEditingSubject(null);
    setEditSubjectName('');
    setEditMissedCount(0);
  };

  const handleDeleteSubject = (subjectId, subjectName) => {
    if (window.confirm('Are you sure you want to delete this subject?')) {
      dispatch(deleteSubject(subjectId));
      setImages(prev => {
        const newImages = { ...prev };
        delete newImages[subjectName];
        return newImages;
      });
    }
  };

  // Enhanced getClassTypeStyle function with better colors and hover effects
  const getClassTypeStyle = (type) => {
    switch (type) {
      case 'lecture': 
        return 'bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 border-2 border-blue-600 text-blue-50 shadow-lg shadow-blue-900/50 hover:shadow-blue-800/60';
      case 'lab': 
        return 'bg-gradient-to-br from-emerald-900 via-emerald-800 to-emerald-700 border-2 border-emerald-600 text-emerald-50 shadow-lg shadow-emerald-900/50 hover:shadow-emerald-800/60';
      case 'break': 
        return 'bg-gradient-to-br from-amber-900 via-amber-800 to-amber-700 border-2 border-amber-600 text-amber-50 shadow-lg shadow-amber-900/50 hover:shadow-amber-800/60';
      default: 
        return 'bg-gradient-to-br from-zinc-800 to-zinc-700 border-2 border-zinc-600 text-zinc-300 shadow-lg shadow-zinc-800/50 hover:shadow-zinc-700/60 hover:bg-zinc-700';
    }
  };

  const handleEditSlot = (day, time) => {
    setEditingSlot({ day, time });
    const classData = timetable[day]?.[time] || { subject: '', teacher: '', room: '', type: 'lecture' };
    setNewClass(classData);
  };

  const handleSaveSlot = () => {
    if (editingSlot) {
      dispatch(updateTimetableSlot({
        day: editingSlot.day,
        time: editingSlot.time,
        classData: newClass
      }));
      setEditingSlot(null);
      setNewClass({ subject: '', teacher: '', room: '', type: 'lecture' });
    }
  };

  const handleClearSlot = () => {
    if (editingSlot) {
      dispatch(updateTimetableSlot({
        day: editingSlot.day,
        time: editingSlot.time,
        classData: { subject: '', teacher: '', room: '', type: 'lecture' }
      }));
      setEditingSlot(null);
      setNewClass({ subject: '', teacher: '', room: '', type: 'lecture' });
    }
  };

  const renderInteractiveTimetable = () => (
    <div className="relative space-y-4 z-10">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-serif font-bold text-white">Timetable</h3>
        <div className="flex gap-2 text-xs">
          <span className="px-3 py-2 bg-gradient-to-r from-blue-900 to-blue-700 text-blue-100 rounded-full shadow-lg border border-blue-600">
            📚 Lecture
          </span>
          <span className="px-3 py-2 bg-gradient-to-r from-emerald-900 to-emerald-700 text-emerald-100 rounded-full shadow-lg border border-emerald-600">
            🔬 Lab
          </span>
          <span className="px-3 py-2 bg-gradient-to-r from-amber-900 to-amber-700 text-amber-100 rounded-full shadow-lg border border-amber-600">
            ☕ Break
          </span>
        </div>
      </div>
      
      <div className="overflow-x-auto rounded-xl border border-zinc-700 shadow-2xl">
        <table className="w-full border-collapse bg-zinc-900 bg-opacity-90">
          <thead>
            <tr className="bg-gradient-to-r from-zinc-800 to-zinc-700">
              <th className="border border-zinc-600 px-4 py-3 text-left text-white font-serif">Time</th>
              {days.map(day => (
                <th key={day} className="border border-zinc-600 px-4 py-3 text-center text-white font-serif">{day}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {timeSlots.map(time => (
              <tr key={time}>
                <td className="border border-zinc-600 px-4 py-3 font-medium bg-zinc-800 text-white text-sm">
                  {time}
                </td>
                {days.map(day => {
                  const classData = timetable[day]?.[time];
                  const isEditing = editingSlot?.day === day && editingSlot?.time === time;
                  const hasClass = classData && classData.subject && classData.subject.trim() !== '';
                  
                  return (
                    <td key={`${day}-${time}`} className="border border-zinc-600 p-2">
                      {isEditing ? (
                        <div className="space-y-2 bg-zinc-800 p-3 rounded-lg border border-zinc-600">
                          <input
                            className="w-full px-2 py-1 border border-zinc-600 rounded text-xs bg-zinc-700 text-white placeholder-zinc-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Subject"
                            value={newClass.subject}
                            onChange={(e) => setNewClass(prev => ({ ...prev, subject: e.target.value }))}
                          />
                          <input
                            className="w-full px-2 py-1 border border-zinc-600 rounded text-xs bg-zinc-700 text-white placeholder-zinc-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Teacher"
                            value={newClass.teacher}
                            onChange={(e) => setNewClass(prev => ({ ...prev, teacher: e.target.value }))}
                          />
                          <input
                            className="w-full px-2 py-1 border border-zinc-600 rounded text-xs bg-zinc-700 text-white placeholder-zinc-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Room"
                            value={newClass.room}
                            onChange={(e) => setNewClass(prev => ({ ...prev, room: e.target.value }))}
                          />
                          <select
                            className="w-full px-2 py-1 border border-zinc-600 rounded text-xs bg-zinc-700 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            value={newClass.type}
                            onChange={(e) => setNewClass(prev => ({ ...prev, type: e.target.value }))}
                          >
                            <option value="lecture">📚 Lecture</option>
                            <option value="lab">🔬 Lab</option>
                            <option value="break">☕ Break</option>
                          </select>
                          <div className="flex gap-1">
                            <button 
                              onClick={handleSaveSlot} 
                              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 rounded text-xs transition-colors font-medium"
                            >
                              Save
                            </button>
                            <button 
                              onClick={handleClearSlot} 
                              className="flex-1 bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded text-xs transition-colors font-medium"
                            >
                              Clear
                            </button>
                            <button 
                              onClick={() => setEditingSlot(null)} 
                              className="flex-1 bg-zinc-600 hover:bg-zinc-700 text-white px-2 py-1 rounded text-xs transition-colors font-medium"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div 
                          className={`p-3 rounded-lg min-h-[80px] relative group cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-xl ${
                            hasClass ? getClassTypeStyle(classData.type) : 'bg-zinc-800 border-2 border-dashed border-zinc-600 text-zinc-400 hover:border-zinc-500 hover:bg-zinc-750'
                          }`}
                          onClick={() => handleEditSlot(day, time)}
                        >
                          {hasClass ? (
                            <>
                              <div className="font-medium text-sm mb-1">{classData.subject}</div>
                              {classData.teacher && (
                                <div className="text-xs mt-1 opacity-90">👨‍🏫 {classData.teacher}</div>
                              )}
                              {classData.room && (
                                <div className="text-xs opacity-80">🏛️ {classData.room}</div>
                              )}
                              <div className="absolute top-1 right-1 text-xs opacity-75">
                                {classData.type === 'lecture' && '📚'}
                                {classData.type === 'lab' && '🔬'}
                                {classData.type === 'break' && '☕'}
                              </div>
                            </>
                          ) : (
                            <div className="text-center text-xs opacity-60 flex items-center justify-center h-full">
                              <span>+ Add Class</span>
                            </div>
                          )}
                          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 bg-black bg-opacity-70 px-2 py-1 rounded shadow text-xs transition-opacity">
                            {hasClass ? 'Edit' : 'Add'}
                          </div>
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderCardView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 relative z-10">
      {days.map(day => (
        <div key={day} className="bg-zinc-900 bg-opacity-90 border border-zinc-700 rounded-xl p-6 shadow-2xl">
          <h4 className="font-bold text-lg mb-4 text-center bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-lg font-serif">{day}</h4>
          <div className="space-y-3">
            {timeSlots.map(time => {
              const classData = timetable[day]?.[time];
              const hasClass = classData && classData.subject && classData.subject.trim() !== '';
              return (
                <div 
                  key={time} 
                  className={`p-3 rounded-lg border cursor-pointer transition-all duration-300 hover:scale-105 ${
                    hasClass ? getClassTypeStyle(classData.type) : 'bg-zinc-800 border-2 border-dashed border-zinc-600 text-zinc-400 hover:border-zinc-500'
                  } shadow-lg`}
                  onClick={() => handleEditSlot(day, time)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-sm">{time}</span>
                    {hasClass && classData.type && (
                      <span className="text-xs px-2 py-1 bg-black bg-opacity-30 rounded-full flex items-center gap-1">
                        {classData.type === 'lecture' && '📚'}
                        {classData.type === 'lab' && '🔬'}
                        {classData.type === 'break' && '☕'}
                        {classData.type}
                      </span>
                    )}
                  </div>
                  <div className="font-bold text-sm">{hasClass ? classData.subject : 'Empty Slot'}</div>
                  {hasClass && classData.teacher && <div className="text-xs opacity-90">👨‍🏫 {classData.teacher}</div>}
                  {hasClass && classData.room && <div className="text-xs opacity-80">🏛️ {classData.room}</div>}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen">
      {/* COMBINED SECTION: Header + Timetable - Black Background with Colored Lines */}
      <div className="relative bg-black text-white overflow-hidden">
        {/* Curved SVG Background (Red + Purple Lines) */}
        <svg className="absolute top-0 left-0 w-full max-w-[900px] opacity-60 z-0 animate-pulse-slow" viewBox="0 0 900 300" fill="none">
          <path d="M0,150 C150,50 300,250 450,150 C600,50 750,250 900,150" stroke="#FF6B6B" strokeWidth="6" strokeLinecap="round" fill="none" />
        </svg>
        <svg className="absolute bottom-0 right-0 w-full max-w-[900px] opacity-60 z-0 animate-pulse-slow" viewBox="0 0 900 300" fill="none">
          <path d="M0,150 C150,250 300,50 450,150 C600,250 750,50 900,150" stroke="#C084FC" strokeWidth="6" strokeLinecap="round" fill="none" />
        </svg>
        <svg className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-[1200px] opacity-40 z-0" viewBox="0 0 1200 400" fill="none">
          <path d="M0,200 C200,100 400,300 600,200 C800,100 1000,300 1200,200" stroke="#10B981" strokeWidth="4" strokeLinecap="round" fill="none" />
        </svg>

        {/* Header */}
        <div className="relative z-10 text-center py-8 mt-20">
          <h1 className="text-4xl md:text-5xl font-serif font-bold mb-2 text-white">Academic Schedule</h1>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-purple-500 mx-auto rounded-full"></div>
        </div>

        <div className="relative z-10 p-6">
          {/* Timetable Toggle */}
          <div
            className="bg-black bg-opacity-70 backdrop-blur-sm border border-zinc-700 p-6 rounded-xl mb-8 cursor-pointer transition-all duration-300 hover:shadow-xl hover:bg-opacity-80 transform hover:scale-[1.02]"
            onClick={() => setShowTimetable(!showTimetable)}
          >
            <div className="text-center">
              <h2 className="text-xl font-serif font-bold mb-2 text-white">Timetable Management</h2>
              <p className="opacity-90 text-gray-300">Click to {showTimetable ? 'hide' : 'view'} class schedule</p>
            </div>
          </div>

          {/* Timetable Content */}
          {showTimetable && (
            <div className="border border-zinc-700 rounded-xl p-6 mb-8 bg-black bg-opacity-50 backdrop-blur-sm">
              {/* View Toggle */}
              <div className="flex gap-3 mb-6">
                <button
                  onClick={() => dispatch(setTimetableView('table'))}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                    timetableView === 'table' 
                      ? 'bg-blue-600 text-white shadow-lg' 
                      : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                  }`}
                >
                  Table View
                </button>
                <button
                  onClick={() => dispatch(setTimetableView('card'))}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                    timetableView === 'card' 
                      ? 'bg-blue-600 text-white shadow-lg' 
                      : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                  }`}
                >
                  Card View
                </button>
              </div>

              {/* Render selected view */}
              {timetableView === 'table' && renderInteractiveTimetable()}
              {timetableView === 'card' && renderCardView()}
            </div>
          )}
        </div>
      </div>


      {/* SECTION 2: SUBJECT MANAGEMENT SECTION - Classic Professional Design */}
      {/* SECTION 2: SUBJECT MANAGEMENT SECTION - Classic Professional Design */}
      <div className="bg-[#e4e5e0]">
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          {/* Subject Management Header */}
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl font-serif font-bold text-gray-800 mb-3 sm:mb-4">Academic Subjects</h2>
            <div className="w-16 sm:w-20 h-0.5 bg-gradient-to-r from-gray-400 to-gray-600 mx-auto mb-4 sm:mb-6"></div>
            <p className="text-gray-600 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed px-4">
              Manage your academic subjects, track attendance, and maintain organized notes for effective learning.
            </p>
          </div>

          {/* Add Subject Card */}
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 sm:p-6 lg:p-8 mb-6 sm:mb-8 hover:shadow-xl transition-all duration-300">
            <h3 className="text-lg sm:text-xl font-serif font-semibold text-gray-800 mb-4 sm:mb-6">Add New Subject</h3>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-end">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Subject Name</label>
                <input
                  className="w-full border border-gray-300 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg text-gray-700 placeholder-gray-400 focus:outline-none focus:border-gray-500 focus:ring-2 focus:ring-gray-200 transition-all duration-200 bg-white font-medium text-sm sm:text-base"
                  type="text"
                  placeholder="e.g., Mathematics, Physics, Literature..."
                  value={newSubject}
                  onChange={(e) => setNewSubject(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddSubject()}
                />
              </div>
              <button 
                onClick={handleAddSubject} 
                className="bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-800 hover:to-gray-900 text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all duration-300 text-sm sm:text-base whitespace-nowrap"
              >
                Add Subject
              </button>
            </div>
          </div>

          {/* Subject Cards */}
          <div className="space-y-4 sm:space-y-6">
            {subjects.map(sub => (
              <div key={sub.id} className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300">
                {editingSubject === sub.id ? (
                  <div className="p-4 sm:p-6 lg:p-8 bg-gradient-to-r from-amber-50 to-orange-50">
                    <h4 className="text-base sm:text-lg font-serif font-semibold text-gray-800 mb-4 sm:mb-6">Edit Subject Details</h4>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
                      <div className="lg:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Subject Name</label>
                        <input
                          className="w-full border border-gray-300 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all duration-200 bg-white font-medium text-sm sm:text-base"
                          type="text"
                          value={editSubjectName}
                          onChange={(e) => setEditSubjectName(e.target.value)}
                          placeholder="Subject Name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Missed Classes</label>
                        <input
                          className="w-full border border-gray-300 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all duration-200 bg-white font-medium text-center text-sm sm:text-base"
                          type="number"
                          min="0"
                          value={editMissedCount}
                          onChange={(e) => setEditMissedCount(parseInt(e.target.value) || 0)}
                        />
                      </div>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                      <button 
                        onClick={handleUpdateSubject} 
                        className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold transition-all duration-300 text-sm sm:text-base"
                      >
                        Save Changes
                      </button>
                      <button 
                        onClick={handleCancelEdit} 
                        className="bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold transition-all duration-300 text-sm sm:text-base"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 sm:p-6 lg:p-8">
                    <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start cursor-pointer" onClick={() => dispatch(toggleExpand(sub.id))}>
                      <div className="flex-1 mb-4 lg:mb-0">
                        <h3 className="text-xl sm:text-2xl font-serif font-bold text-gray-800 mb-2">{sub.name}</h3>
                        <p className="text-gray-500 text-xs sm:text-sm">Click to {sub.expanded ? 'hide' : 'view'} notes</p>
                      </div>
                      
                      <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 lg:gap-6">
                        {images[sub.name] && (
                          <img src={images[sub.name]} alt="Subject" className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg shadow-md object-cover border border-gray-200" />
                        )}
                        
                        <div className="bg-gradient-to-br from-slate-50 to-slate-100 px-4 sm:px-6 py-3 sm:py-4 rounded-xl border border-slate-200 shadow-inner">
                          <div className="flex items-center gap-3 sm:gap-4">
                              <button 
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  dispatch(decrementMissed(sub.id)); 
                                }} 
                                className="w-8 h-8 sm:w-9 sm:h-9 bg-gradient-to-br from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white rounded-lg transition-all duration-300 flex items-center justify-center font-medium shadow-sm hover:shadow-md"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                                </svg>
                              </button>
                              <div className="text-center">
                                <div className="text-xs text-slate-500 font-medium uppercase tracking-wider mb-1">Absences</div>
                                <div className="text-xl sm:text-2xl font-bold text-slate-700">{sub.missedCount}</div>
                              </div>
                              <button 
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  dispatch(incrementMissed(sub.id)); 
                                }} 
                                className="w-8 h-8 sm:w-9 sm:h-9 bg-gradient-to-br from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white rounded-lg transition-all duration-300 flex items-center justify-center font-medium shadow-sm hover:shadow-md"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                              </button>
                            </div>
                        </div>
                        
                        <div className="flex flex-row sm:flex-row gap-2 w-full sm:w-auto">
                          <button 
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              handleEditSubject(sub); 
                            }} 
                            className="group bg-[#ecece9] hover:from-slate-200 hover:to-slate-300 text-slate-700 hover:text-slate-800 px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all duration-300 border border-slate-300 hover:border-slate-400 shadow-sm hover:shadow-md flex items-center justify-center gap-1 sm:gap-2 flex-1 sm:flex-none"
                          >
                            <svg className="w-3 h-3 sm:w-4 sm:h-4 transition-transform group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            <span className="hidden sm:inline">Edit</span>
                          </button>
                          <button 
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              handleDeleteSubject(sub.id, sub.name); 
                            }} 
                            className="group bg-[#ecece9] to-rose-100 hover:from-rose-100 hover:to-rose-200 text-rose-700 hover:text-rose-800 px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all duration-300 border border-rose-200 hover:border-rose-300 shadow-sm hover:shadow-md flex items-center justify-center gap-1 sm:gap-2 flex-1 sm:flex-none"
                          >
                            <svg className="w-3 h-3 sm:w-4 sm:h-4 transition-transform group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            <span className="hidden sm:inline">Remove</span>
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {sub.expanded && (
                      <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-gray-200">
                        <h4 className="text-base sm:text-lg font-serif font-semibold text-gray-800 mb-3 sm:mb-4">Subject Notes</h4>
                        <textarea
                          className="w-full p-4 sm:p-6 border border-gray-300 rounded-lg focus:outline-none focus:border-gray-500 focus:ring-2 focus:ring-gray-200 resize-none bg-gray-50 text-gray-700 font-medium leading-relaxed transition-all duration-200 text-sm sm:text-base"
                          placeholder="Write your notes, important points, or reminders for this subject..."
                          value={sub.notes || ''}
                          onChange={(e) => dispatch(updateNotes({ id: sub.id, notes: e.target.value }))}
                          rows={4}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            
            {subjects.length === 0 && (
              <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-8 sm:p-12">
                <div className="text-center">
                  <h3 className="text-lg sm:text-xl font-serif font-semibold text-gray-700 mb-3">No Subjects Yet</h3>
                  <p className="text-gray-500 max-w-md mx-auto leading-relaxed text-sm sm:text-base px-4">
                    Start building your academic portfolio by adding your first subject. Track attendance, maintain notes, and stay organized.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassesPage;